import { useRef, useState } from "react"
import styles from "./Posts.module.scss"
import { Comment, Reply } from "../../types"
import { PlusMinusInput } from "./PlusMinusInput"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { addReply, deleteComment, editComment } from "./postsSlice"
import { YouLabel } from "../components/YouLabel"
import { TimeAgo } from "./TimeAgo"
import { ReplyBtn } from "./ReplyBtn"
import { EditInput } from "./EditInput"
import { ReplyInput } from "./ReplyInput"
import { DeleteModal } from "../components/DeleteModal"
import { DeleteEditBtns } from "./DeleteEditBtns"
import { CommentReply } from "./CommentReply"
import { selectUserById } from "../users/usersSlice"
import { getPublicUserNamePlaceholder } from "../../utils/miscellaneous"
import { UserAvatar } from "../components/UserAvatar"

export const CommentComponent = ({
  postId,
  comment,
  authorIdToReplyTo,
  windowSize,
}: {
  postId: string
  comment: Comment
  authorIdToReplyTo: React.MutableRefObject<string>
  windowSize: {
    width: number
    height: number
  }
}) => {
  const dispatch = useAppDispatch()

  const currentUser = useAppSelector((state) => state.users.currentUser)
  const [commentReplyInputOpen, setCommentReplyInputOpen] =
    useState<boolean>(false)
  const [replyReplyInputOpen, setReplyReplyInputOpen] = useState<boolean>(false)
  const commentReplyInputTextareaRef = useRef(null)
  const replyReplyInputTextareaRef = useRef(null)
  const [isEditing, setIsEditing] = useState(false)
  const [textareaValue, setTextareaValue] = useState(comment.content)
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)

  const editCommentDispatcher = ({
    content,
    score,
    commentId,
  }: {
    content?: string
    score?: string
    commentId: string
  }) => {
    if (postId) {
      dispatch(
        editComment({
          content,
          score,
          postId: postId,
          commentId,
        }),
      )
    }
  }
  const author = useAppSelector((state) =>
    selectUserById(state, comment.authorId),
  )

  return (
    <>
      <div data-testid="postComment" className={styles.commentContainer}>
        <div className={styles.commentBody}>
          {window.innerWidth > 900 && (
            <PlusMinusInput
              disabled={!currentUser}
              score={Number(comment.score)}
              onPlusMinusClickHandler={(valueToIncrement) =>
                editCommentDispatcher({
                  score: String(comment.score + valueToIncrement),
                  commentId: comment._id,
                })
              }
            />
          )}
          <div className={styles.userLabelAndContentContainer}>
            <div className={styles.postAuthorImgNameTimeAgo}>
              <UserAvatar userId={comment.authorId} size={34} />
              <span className={styles.userName}>
                {author?.name || getPublicUserNamePlaceholder(comment.authorId)}
              </span>
              {currentUser && (
                <YouLabel
                  authorId={comment.authorId}
                  currentUserId={currentUser.id}
                />
              )}
              <TimeAgo timestamp={comment.createdAt} />
              {window.innerWidth > 900 &&
                (author?.id !== currentUser?.id ? (
                  <ReplyBtn
                    authorIdToReplyTo={authorIdToReplyTo}
                    textareaToFocus={commentReplyInputTextareaRef}
                    inputOpenSet={setCommentReplyInputOpen}
                    authorId={comment.authorId}
                  />
                ) : (
                  <DeleteEditBtns
                    onDeleteClicked={() => {
                      setDeleteModalOpened(true)
                    }}
                    onEditClicked={() => {
                      setIsEditing(!isEditing)
                    }}
                  />
                ))}
            </div>
            {!isEditing ? (
              <div data-testid="content" className={styles.contentContainer}>
                <p className={styles.content}>{comment.content}</p>
              </div>
            ) : (
              <EditInput
                entityValue={comment.content}
                textareaValue={textareaValue}
                setTextareaValue={setTextareaValue}
                setIsEditing={setIsEditing}
                onUpdate={() => {
                  dispatch(
                    editComment({
                      content: textareaValue,
                      postId,
                      commentId: comment._id,
                    }),
                  )
                  setIsEditing(false)
                }}
              />
            )}
            <div className={styles.plusMinusDeleteEditReplyReplacedContainer}>
              {windowSize.width < 900 && (
                <PlusMinusInput
                  disabled={!currentUser}
                  score={Number(comment.score)}
                  onPlusMinusClickHandler={(valueToIncrement) =>
                    editCommentDispatcher({
                      score: String(comment.score + valueToIncrement),
                      commentId: comment._id,
                    })
                  }
                />
              )}
              {window.innerWidth < 900 &&
                (comment.authorId !== currentUser?.id ? (
                  <ReplyBtn
                    authorIdToReplyTo={authorIdToReplyTo}
                    textareaToFocus={commentReplyInputTextareaRef}
                    inputOpenSet={setCommentReplyInputOpen}
                    authorId={comment.authorId}
                  />
                ) : (
                  <DeleteEditBtns
                    onDeleteClicked={() => {
                      setDeleteModalOpened(true)
                    }}
                    onEditClicked={() => {
                      setIsEditing(!isEditing)
                    }}
                  />
                ))}
            </div>
          </div>
        </div>
        <div className={styles.repliesContainer}>
          {comment.replies.map((reply: Reply) => {
            return (
              <CommentReply
                postId={postId}
                authorIdToReplyTo={authorIdToReplyTo}
                key={reply._id}
                reply={reply}
                commentId={comment._id}
                setReplyReplyInputOpen={setReplyReplyInputOpen}
                replyReplyInputTextareaRef={replyReplyInputTextareaRef}
                windowSize={windowSize}
              />
            )
          })}
          {replyReplyInputOpen && (
            <ReplyInput
              placeholder="Add a reply"
              textareaRef={replyReplyInputTextareaRef}
              btnText="REPLY"
              onClickOutside={() => setReplyReplyInputOpen(false)}
              onSendClick={(content: string) => {
                if (currentUser) {
                  dispatch(
                    addReply({
                      postId,
                      commentId: comment._id,
                      content: content,
                      authorId: currentUser.id,
                      repliedTo: authorIdToReplyTo.current,
                    }),
                  )
                  setReplyReplyInputOpen(false)
                }
              }}
            />
          )}
        </div>
        {commentReplyInputOpen && (
          <ReplyInput
            placeholder="Add a reply"
            textareaRef={commentReplyInputTextareaRef}
            btnText="REPLY"
            onClickOutside={() => setCommentReplyInputOpen(false)}
            onSendClick={(content: string) => {
              if (currentUser) {
                dispatch(
                  addReply({
                    postId,
                    commentId: comment._id,
                    content: content,
                    authorId: currentUser.id,
                    repliedTo: authorIdToReplyTo.current,
                  }),
                )
                setCommentReplyInputOpen(false)
              }
            }}
          />
        )}
      </div>
      {deleteModalOpened && (
        <DeleteModal
          setModalOpened={setDeleteModalOpened}
          onConfirmation={() => {
            dispatch(
              deleteComment({
                postId: postId,
                commentId: comment._id,
              }),
            )
          }}
          titleText="Delete comment"
          cancelBtnText="NO, CANCEL"
          confirmBtnText="YES, DELETE"
        />
      )}
    </>
  )
}
